/**
 * CypAdhdBoaCapturePanel.js
 *
 * Behavioural Observation Appointment (BOA) capture at the top of the CYP
 * MDT Review (cypAdhdFlow.boaCapture === 'mdtReviewTab' — TOC, 23 Sept
 * standup). Asks whether a BOA took place and, if so, captures the
 * four-paragraph summary from TOC's BOA guidance verbatim: context, core
 * observations, client voice, and differentials/signposting/feedback.
 *
 * CAPTURE ONLY by explicit decision (Tom, 23 Sept): nothing here is wired
 * into report generation yet — the report section follows once TOC's
 * template decisions land. The BOA is conducted by someone who may never
 * sign in to the platform; the clinician recording it here is responsible
 * for the conversation with the observer.
 *
 * Self-contained: reads the modular patient context and the workflow stamp
 * itself and renders nothing unless the stamp asks for the tab, so the host
 * page (0_DDReview) needs only a single-line insertion.
 *
 * Read-only gating: the host passes `canEdit` (assigned DD clinician and not
 * previewing). When false the Yes/No toggle and paragraphs are disabled and
 * `persist` is a no-op, so a preview or a non-assigned viewer can read the
 * recorded BOA but never write to the stage doc. Defaults to true so the
 * panel stays writable for any caller that predates the prop.
 *
 * Data shape, persisted wholesale under the DD stage doc's `boa` field:
 *   { tookPlace: bool|null, context: '', coreObservations: '',
 *     clientVoice: '', differentialsSignposting: '' }
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';

const DD_STAGE_KEY = 'diagnosticDecision';

// The four BOA summary paragraphs, headings and captions from TOC's BOA
// guidance document (23 Sept). Captions keep the guidance verbatim, trimmed
// to the ADHD pathway where the document branches by pathway.
export const CYP_BOA_PARAGRAPHS = Object.freeze([
  Object.freeze({
    id: 'context',
    heading: 'Context',
    caption:
      "The setting, the client's presentation on arrival, engagement and "
      + 'rapport, and any contextual factors relevant to interpreting the '
      + 'session.',
  }),
  Object.freeze({
    id: 'coreObservations',
    heading: 'Core observations',
    caption:
      'Attention/concentration and activity/impulsivity. Use descriptive '
      + 'language grounded in specific, observed behaviour.',
  }),
  Object.freeze({
    id: 'clientVoice',
    heading: 'Client voice',
    caption:
      'Strengths, interests, experiences of school/work, and views on a '
      + 'potential diagnosis, including direct quotes where available. '
      + 'Include masking/camouflaging findings where this was explored.',
  }),
  Object.freeze({
    id: 'differentialsSignposting',
    heading: 'Differentials, signposting and feedback',
    caption:
      'Alternative explanations considered, any signposting provided, and a '
      + 'brief note on process feedback gathered.',
  }),
]);

export const CYP_BOA_GUIDANCE =
  'Report language should remain descriptive rather than diagnostic: the '
  + 'BOA is one source of evidence to be triangulated with the developmental '
  + 'history and observation at MDT, not a standalone determination.';

const EMPTY_BOA = Object.freeze({
  tookPlace: null,
  context: '',
  coreObservations: '',
  clientVoice: '',
  differentialsSignposting: '',
});

export function normalizeBoaData(stored) {
  if (!stored || typeof stored !== 'object') return { ...EMPTY_BOA };
  return {
    tookPlace: typeof stored.tookPlace === 'boolean' ? stored.tookPlace : null,
    context: stored.context || '',
    coreObservations: stored.coreObservations || '',
    clientVoice: stored.clientVoice || '',
    differentialsSignposting: stored.differentialsSignposting || '',
  };
}

export default function CypAdhdBoaCapturePanel({ canEdit = true }) {
  const { header, assessmentStages, updateAssessmentStage } =
    useContext(ModularPatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);

  const ddStage = assessmentStages?.[DD_STAGE_KEY];
  const [boaData, setBoaData] = useState(() => normalizeBoaData(ddStage?.boa));
  useEffect(() => {
    setBoaData(normalizeBoaData(ddStage?.boa));
  }, [ddStage?.boa]);

  // Read-only viewers never reach Firestore: the controls below are disabled
  // too, but the guard here is the one that actually protects the data.
  const persist = useCallback((nextData) => {
    if (!canEdit) return;
    setBoaData(nextData);
    updateAssessmentStage(DD_STAGE_KEY, { boa: nextData });
  }, [canEdit, updateAssessmentStage]);

  // The workflow stamp decides whether this org captures the BOA here at
  // all — everything above the guard must stay hook-order safe.
  if (!wf.cypShowBoaMdtTab) return null;

  const boaRequired = header?.customFields?.boaRequired === true;
  const childName = header?.firstName || 'this child';

  const handleTookPlace = (tookPlace) => {
    persist({ ...boaData, tookPlace });
  };

  const handleParagraphChange = (id, value) => {
    persist({ ...boaData, [id]: value });
  };

  const toggleBase =
    'px-4 py-2 min-h-[44px] text-sm font-medium border first:rounded-l-md last:rounded-r-md focus:outline-none focus:ring-2 focus:ring-violet-500';

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6 text-left">
      <h3 className="text-lg font-semibold mb-1">Behavioural Observation Appointment</h3>
      {boaRequired && (
        <p className="text-sm font-medium text-gray-800 mb-2">
          A Behavioural Observation Appointment is required under this
          patient&apos;s contract.
        </p>
      )}
      <p className="text-sm text-gray-600 mb-4">
        The BOA summary is shared ahead of the MDT. {CYP_BOA_GUIDANCE}
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="text-sm font-medium text-gray-900">
          Was {childName} seen for a Behavioural Observation Appointment?
        </span>
        <div className="inline-flex" role="group" aria-label="BOA took place">
          <button
            type="button"
            onClick={() => handleTookPlace(true)}
            aria-pressed={boaData.tookPlace === true}
            disabled={!canEdit}
            className={`${toggleBase} ${
              boaData.tookPlace === true
                ? 'bg-violet-600 border-violet-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${!canEdit ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => handleTookPlace(false)}
            aria-pressed={boaData.tookPlace === false}
            disabled={!canEdit}
            className={`${toggleBase} -ml-px ${
              boaData.tookPlace === false
                ? 'bg-gray-700 border-gray-700 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${!canEdit ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            No
          </button>
        </div>
      </div>

      {boaData.tookPlace === true && (
        <div className="space-y-4">
          {CYP_BOA_PARAGRAPHS.map((paragraph, index) => (
            <div key={paragraph.id}>
              <label
                htmlFor={`boa-${paragraph.id}`}
                className="block text-sm font-semibold text-gray-900 mb-1"
              >
                {`Paragraph ${index + 1} — ${paragraph.heading}`}
              </label>
              <p className="text-xs text-gray-500 mb-2">{paragraph.caption}</p>
              <textarea
                id={`boa-${paragraph.id}`}
                value={boaData[paragraph.id] || ''}
                onChange={(e) => handleParagraphChange(paragraph.id, e.target.value)}
                readOnly={!canEdit}
                rows={4}
                className={`w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'
                }`}
              />
            </div>
          ))}
          <p className="text-xs text-gray-500">
            This summary is not included in generated reports yet — report
            configuration for the BOA follows separately.
          </p>
        </div>
      )}
    </div>
  );
}
